import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertService } from '../services/alert-service';
import { alertsKeys } from './alerts-keys';
import type { Alert, AlertStatus, UpdateAlertInput } from '../types/alert.types';

export interface AlertsResult {
  isLoading: boolean;
  isError: boolean;
  alerts: Alert[];
  refresh: () => void;
}

/**
 * Loads the agency's recurring alerts for the authenticated broker.
 *
 * @param agencyId - The agency (workspace) whose alerts to load.
 * @param status - Optional status filter ('active' | 'inactive' | 'all').
 * @returns The view-model: flags, rows, and a refresh action.
 */
export function useAlerts(agencyId: string, status = 'all'): AlertsResult {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: alertsKeys.list(agencyId, status),
    queryFn: () => AlertService.listAlerts(agencyId, status === 'all' ? undefined : status),
    enabled: Boolean(agencyId),
  });

  return {
    isLoading: query.isLoading,
    isError: query.isError,
    alerts: query.data ?? [],
    refresh: () => queryClient.invalidateQueries({ queryKey: alertsKeys.all(agencyId) }),
  };
}

/**
 * Mutations for an alert's status toggle and deletion.
 *
 * The toggle persists via `PUT /alerts/{id}` and the list is invalidated so a
 * re-fetch reflects the real status — never cosmetic-only state (AC-ALERT-3).
 *
 * @param agencyId - The agency (workspace) the alerts belong to.
 * @returns `toggle` and `remove` mutation handles plus pending flags.
 */
export function useAlertMutations(agencyId: string) {
  const queryClient = useQueryClient();

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: alertsKeys.all(agencyId) });

  const toggle = useMutation({
    mutationFn: ({ alertId, status }: { alertId: string; status: AlertStatus }) => {
      const body: UpdateAlertInput = { status };
      return AlertService.updateAlert(agencyId, alertId, body);
    },
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (alertId: string) => AlertService.deleteAlert(agencyId, alertId),
    onSuccess: invalidate,
  });

  return {
    toggle: (alertId: string, status: AlertStatus) => toggle.mutate({ alertId, status }),
    remove: (alertId: string) => remove.mutate(alertId),
    isToggling: toggle.isPending,
    isRemoving: remove.isPending,
  };
}
